'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { Toaster } from 'react-hot-toast';
import { APP_CONFIG } from '@/lib/config';
import { PreferencesLoader } from '@/components/PreferencesLoader';
import { useAuthStore } from '@/store/auth';

// Pages that must always render dark, regardless of the user's saved theme.
const PUBLIC_PATHS = ['/', '/login', '/register', '/invite'];

function QueryClientBoundary({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: APP_CONFIG.queryClient,
        },
      })
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const pathname = usePathname();

  // Force dark on public routes via next-themes (not an imperative setTheme).
  // When it flips back to undefined on authed routes, next-themes restores the
  // user's stored theme (pre-paint) — no page refresh needed.
  const isPublicPath = PUBLIC_PATHS.some(
    p => pathname === p || pathname?.startsWith(`${p}/`)
  );

  // Key that changes only when the *effective identity* changes (not on token refresh).
  const identityKey = !isAuthenticated || !user
    ? 'anon'
    : `${user.id ?? 'unknown'}:${user.is_connected_as ? `imp:${user.original_user_id ?? 'unknown'}` : 'self'}`;

  const content = (
    <div key={identityKey}>
      <PreferencesLoader />
      {children}
      <Toaster 
        position={APP_CONFIG.toast.position}
        toastOptions={{
          duration: APP_CONFIG.toast.duration,
          style: APP_CONFIG.toast.style,
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#f1f5f9',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f1f5f9',
            },
          },
        }}
      />
    </div>
  );

  return (
    <QueryClientBoundary key={identityKey}>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem
        disableTransitionOnChange
        forcedTheme={isPublicPath ? 'dark' : undefined}
      >
        {content}
      </ThemeProvider>
    </QueryClientBoundary>
  );
}